import React, { useRef, useCallback, useState, useMemo, useImperativeHandle, forwardRef, useEffect } from 'react';
import type Handsontable from 'handsontable';
import { useIntl, dynamic } from 'umi';
import TableContextMenu, { IMenuState } from './components/TableContextMenu';
import AddRowColButton from '../AddRowColButton';
import AIColumnPopover, { AIColumnActionType } from '../AIColumnPopover';
import { RowColType, MAX_EXCEL_ROWS } from '../../constants';
import type { ITableActionsBarRef } from '../TableActionsBar';
import { updateColumnsWithTypeDetection, detectValueType } from '../../utils';
import { createNewColumn } from './utils';
import { createFirstColumnContent } from './firstColumnRenderer';
import { useResizeHandles } from './hooks/useResizeHandles';
import { useRowDragDelegation } from './hooks/useRowDragDelegation';
import { useMenuActions } from './hooks/useMenuActions';
import type {
  IAIColumnPopoverState,
  IColumnConfig,
  ISmartTableGridRef,
  TTableRow,
} from './types';
import rowDragIcon from '@/pages/aigc/assets/svg/agent-row-drag.svg';
import './index.scss';

// 重新导出，保持外部 import 路径不变（外部仍可 from '.../SmartTableGrid'）
export type { IColumnConfig, TTableRow, ISmartTableGridRef } from './types';

// 动态导入 handsontable 相关模块，避免静态打包进主 chunk
const HotTable = dynamic({
  async loader() {
    const { registerAllModules } = await import(/* webpackChunkName: "handsontable" */ 'handsontable/registry');
    await import(/* webpackChunkName: "handsontable" */ 'handsontable/dist/handsontable.full.min.css');
    registerAllModules();
    const { HotTable: HotTableComponent } = await import(/* webpackChunkName: "handsontable" */ '@handsontable/react');
    return HotTableComponent;
  },
});

interface ISmartTableGridProps {
  /** 初始表格数据 */
  initialData: TTableRow[];
  /** 初始列配置 */
  initialColumns: IColumnConfig[];
  /** 工具栏 ref（用于内部自动刷新撤销/重做状态） */
  toolbarRef?: React.RefObject<ITableActionsBarRef>;
  /** 表格名称（用于弹层显示） */
  tableName?: string;
  /** 工作表名称（用于弹层显示） */
  sheetName?: string;
  /** 数据变化回调（可选，用于外部同步） */
  onDataChange?: (data: TTableRow[], columns: IColumnConfig[]) => void;
  /** AI 修改列数据条件回调（可选） */
  onAIModifyColumnCondition?: (colIndex: number, column: IColumnConfig, prompt: string) => void;
  /** AI 调整数据格式回调（可选） */
  onAIAdjustDataFormat?: (colIndex: number, column: IColumnConfig, prompt: string) => void;
  /** 智能填充行回调（可选） */
  onSmartFillRow?: (values: any) => void;
  /** 智能填充列回调（可选） */
  onSmartFillColumn?: (values: any) => void;
  /** 是否为只读模式（分享场景下为 true，禁用所有编辑功能） */
  readonly?: boolean;
}

/**
 * 智能表格网格组件
 * 数据操作方法已下沉到组件内部，外部通过 ref 获取/设置数据
 */
const SmartTableGrid = forwardRef<ISmartTableGridRef, ISmartTableGridProps>(({
  initialData,
  initialColumns,
  toolbarRef,
  tableName,
  sheetName,
  onDataChange,
  onAIModifyColumnCondition,
  onAIAdjustDataFormat,
  onSmartFillRow,
  onSmartFillColumn,
  readonly = false,
}, ref) => {
  const { formatMessage } = useIntl();
  const defaultTableName = formatMessage({ id: 'dba_smart_table_table' });
  // 存储 Handsontable 实例（通过 afterInit 钩子获取，避免 umi dynamic ref 被劫持的问题）
  const hotInstanceRef = useRef<Handsontable | null>(null);
  
  // 内部状态管理
  const [tableData, setTableData] = useState<TTableRow[]>(initialData);
  const [columns, setColumns] = useState<IColumnConfig[]>(initialColumns);

  // 标记是否为内部编辑导致的变更（避免 props 同步回来时重复 setState + render）
  const isInternalEditRef = useRef(false);

  // 用于存储复制的文本内容，解决右键菜单粘贴时焦点丢失导致无法从系统剪贴板读取的问题
  const clipboardDataRef = useRef<{ text: string; html: string; }>({ text: '', html: '' });

  // 当外部初始数据变化时，同步更新内部状态
  // 仅当变更来自外部（如切换 sheet、AI 返回新数据）时才同步
  useEffect(() => {
    if (isInternalEditRef.current) {
      // 内部编辑导致的 props 回传，跳过同步（数据已是最新）
      isInternalEditRef.current = false;
      return;
    }
    setTableData(initialData);
  }, [initialData]);

  useEffect(() => {
    if (isInternalEditRef.current) {
      return;
    }
    setColumns(initialColumns);
  }, [initialColumns]);

  // 初始数据或列配置变化后，手动触发 Handsontable 重新渲染
  // 仅当外部数据更新时需要（内部编辑时 Handsontable 自己已更新视图）
  const prevInitialDataRef = useRef(initialData);
  const prevInitialColumnsRef = useRef(initialColumns);
  useEffect(() => {
    // 只在外部数据真正变化时才触发 render（排除内部编辑的回传）
    const dataChanged = prevInitialDataRef.current !== initialData;
    const colsChanged = prevInitialColumnsRef.current !== initialColumns;
    prevInitialDataRef.current = initialData;
    prevInitialColumnsRef.current = initialColumns;

    if ((dataChanged || colsChanged) && !isInternalEditRef.current) {
      const hot = hotInstanceRef.current;
      if (hot) {
        requestAnimationFrame(() => {
          hot.render();
        });
      }
    }
  }, [initialData, initialColumns]);

  const [contextMenu, setContextMenu] = useState<IMenuState>({
    visible: false,
    x: 0,
    y: 0,
    type: 'row',
    selectedIndices: [],
    cellCoords: undefined,
  });
  const [iconSelectedRowIndex, setIconSelectedRowIndex] = useState<number>(-1);
  // const [sortState, setSortState] = useState<ISortState>(INITIAL_SORT_STATE);

  // AI 列操作弹层状态
  const [aiColumnPopover, setAIColumnPopover] = useState<IAIColumnPopoverState>({
    visible: false,
    actionType: AIColumnActionType.MODIFY_CONDITION,
    position: { x: 0, y: 0 },
    colIndex: -1,
  });

  // [性能优化] 已移除重复的 useEffect 同步逻辑
  // initialData/initialColumns 的同步已由上方带 isInternalEditRef 守卫的 useEffect 处理（L129-164）
  // 此处原有的未受保护的 setTableData/setColumns/hot.render() 会导致：
  // 1. 每次数据变化触发 2× setState + 2× hot.render()
  // 2. 内部编辑时也触发无效同步，造成严重卡顿

  // 判断是否达到最大行数限制
  const isMaxRowsReached = useMemo(() => tableData.length >= MAX_EXCEL_ROWS, [tableData.length]);

  // "添加行"和"添加列"按钮容器的 ref，用于直接操作 DOM class 避免重渲染
  const addRowButtonRef = useRef<HTMLDivElement>(null);
  const addColButtonRef = useRef<HTMLDivElement>(null);

  // [性能优化] 使用 ref 存储最新的 tableData/columns/onDataChange
  // 供 syncData/useImperativeHandle/menuActions 等通过 ref 访问最新数据，避免依赖频繁变化的 state
  const tableDataRef = useRef(tableData);
  const columnsRef = useRef(columns);
  const onDataChangeRef = useRef(onDataChange);
  useEffect(() => { tableDataRef.current = tableData }, [tableData]);
  useEffect(() => { columnsRef.current = columns }, [columns]);
  useEffect(() => { onDataChangeRef.current = onDataChange }, [onDataChange]);

  // 获取实例（直接从 hotInstanceRef 获取，不依赖 dynamic 组件的 ref 转发）
  const getHotInstance = useCallback((): Handsontable | null => hotInstanceRef.current, []);

  /** 获取 UndoRedo 插件实例 */
  const getUndoRedoPlugin = useCallback(() => {
    const hot = getHotInstance();
    return hot?.getPlugin('undoRedo') as any;
  }, [getHotInstance]);

  // 检查撤销/重做状态 - 内部自动刷新工具栏状态
  const checkUndoRedo = useCallback(() => {
    toolbarRef?.current?.refreshUndoRedoState();
  }, [toolbarRef]);

  // 同步数据到内部状态，并通知外部
  // 同时根据数据动态更新列的 sortable 和 type 属性
  const syncData = useCallback((changedColKey?: string) => {
    const hot = getHotInstance();
    if (hot) {
      // 标记为内部编辑，阻止 useEffect 中的重复同步
      isInternalEditRef.current = true;
      const newData = hot.getSourceData() as TTableRow[];
      setTableData(newData);
      
      // 动态更新列的 sortable 和 type 属性（基于数据类型检测）
      // [性能优化] 通过 ref 访问 columns，避免 syncData 依赖 columns state
      const currentColumns = columnsRef.current;
      const updatedColumns = updateColumnsWithTypeDetection(newData, currentColumns, changedColKey);
      // 检查 sortable 或 type 属性是否有变化
      const hasColumnChange = updatedColumns.some((col, idx) => 
        col.sortable !== currentColumns[idx]?.sortable || col.type !== currentColumns[idx]?.type
      );
      if (hasColumnChange) {
        setColumns(updatedColumns);
        onDataChangeRef.current?.(newData, updatedColumns);
      } else {
        onDataChangeRef.current?.(newData, currentColumns);
      }
    }
  }, [getHotInstance]);

  // ==================== 内部数据操作方法 ====================

  /** 添加行 */
  const handleAddRow = useCallback(() => {
    const newRow: TTableRow = {};
    columns.forEach(col => {
      newRow[col.data] = null;
    });
    const newData = [...tableData, newRow];
    setTableData(newData);
    onDataChange?.(newData, columns);
  }, [columns, tableData, onDataChange]);

  /**
   * 将列结构变更注册到 Handsontable 原生 UndoRedo 栈
   * 利用插件内置的 done() 方法推入自定义 action，实现统一的 undo/redo
   * action.undo() / action.redo() 中直接操作 React state
   */
  const pushColumnAction = useCallback((
    prevData: TTableRow[],
    prevColumns: IColumnConfig[],
    nextData: TTableRow[],
    nextColumns: IColumnConfig[],
  ) => {
    const plugin = getUndoRedoPlugin();
    if (!plugin) return;

    // 应用新状态
    setTableData(nextData);
    setColumns(nextColumns);
    onDataChange?.(nextData, nextColumns);

    // 构造自定义 action 并推入 UndoRedo 栈
    plugin.done(() => ({
      actionType: 'custom_column',
      undo: (_hot: any, undoneCallback: () => void) => {
        setTableData(prevData);
        setColumns(prevColumns);
        onDataChange?.(prevData, prevColumns);
        requestAnimationFrame(() => checkUndoRedo());
        undoneCallback();
      },
      redo: (_hot: any, redoneCallback: () => void) => {
        setTableData(nextData);
        setColumns(nextColumns);
        onDataChange?.(nextData, nextColumns);
        requestAnimationFrame(() => checkUndoRedo());
        redoneCallback();
      },
    }));

    requestAnimationFrame(() => checkUndoRedo());
  }, [getUndoRedoPlugin, onDataChange, checkUndoRedo]);

  /** 添加空白列（在末尾插入） */
  const handleAddColumn = useCallback(() => {
    const newCol = createNewColumn(Date.now(), 0);
    const newColumns = [...columns, newCol];
    const newData = tableData.map(row => ({ ...row, [newCol.data]: null }));
    pushColumnAction(tableData, columns, newData, newColumns);
  }, [columns, tableData, pushColumnAction]);

  // 暴露方法给父组件
  // [性能优化] 通过 ref 访问 tableData/columns/onDataChange，移除对频繁变化 state 的依赖
  // 避免每次编辑导致 ref 对象重建
  useImperativeHandle(ref, () => ({
    undo: () => {
      const plugin = getUndoRedoPlugin();
      if (plugin?.isUndoAvailable()) {
        plugin.undo();
        requestAnimationFrame(() => checkUndoRedo());
      }
    },
    redo: () => {
      const plugin = getUndoRedoPlugin();
      if (plugin?.isRedoAvailable()) {
        plugin.redo();
        requestAnimationFrame(() => checkUndoRedo());
      }
    },
    isUndoAvailable: () => getUndoRedoPlugin()?.isUndoAvailable() || false,
    isRedoAvailable: () => getUndoRedoPlugin()?.isRedoAvailable() || false,
    getData: () => tableDataRef.current,
    setData: (data: TTableRow[]) => {
      setTableData(data);
      onDataChangeRef.current?.(data, columnsRef.current);
    },
    getColumns: () => columnsRef.current,
    setColumns: (newColumns: IColumnConfig[]) => {
      setColumns(newColumns);
      onDataChangeRef.current?.(tableDataRef.current, newColumns);
    },
    setTableState: (data: TTableRow[], newColumns: IColumnConfig[]) => {
      setTableData(data);
      setColumns(newColumns);
      onDataChangeRef.current?.(data, newColumns);
    },
  }), [getUndoRedoPlugin, checkUndoRedo]);

  // 保存 outsideClickDeselects 原始值的 ref
  const origOutsideClickDeselectsRef = useRef<boolean | ((target: HTMLElement) => boolean) | undefined>(undefined);

  /**
   * 打开右键菜单时禁用 outsideClickDeselects
   * 与 Handsontable 内置右键菜单行为一致：菜单打开时阻止外部点击取消选区
   */
  const lockSelection = useCallback(() => {
    const hot = getHotInstance();
    if (hot) {
      const settings = hot.getSettings();
      origOutsideClickDeselectsRef.current = settings.outsideClickDeselects;
      settings.outsideClickDeselects = false;
    }
  }, [getHotInstance]);

  /** 关闭右键菜单时恢复 outsideClickDeselects */
  const unlockSelection = useCallback(() => {
    const hot = getHotInstance();
    if (hot && origOutsideClickDeselectsRef.current !== undefined) {
      hot.getSettings().outsideClickDeselects = origOutsideClickDeselectsRef.current;
      origOutsideClickDeselectsRef.current = undefined;
    }
  }, [getHotInstance]);

  // 关闭菜单
  const closeContextMenu = useCallback(() => {
    unlockSelection();
    setContextMenu(prev => ({ ...prev, visible: false }));
  }, [unlockSelection]);

  // 转换列配置
  // 当列处于排序状态时，设置为只读（不可编辑）
  // 注意：不设置 title 属性，让 colHeaders 的字母设置（A, B, C...）生效
  // 注意：不在 columns 中设置 className，改用 cells 回调实现单元格级别的独立对齐
  const hotColumns = useMemo(() =>
    columns.map((col) => ({
      data: col.data,
      width: col.width || 174,
      type: col.type || 'text',
    })),
  [columns]
  );

  /**
   * 预计算每列的对齐方式（基于该列数据行的实际值类型采样）
   * 跳过第一行（表头行），采样后续数据行来判断列的主要类型
   * 使用 useMemo 缓存，仅在 columns/tableData 变化时重新计算
   */
  const columnAlignClassNames = useMemo(() => {
    return columns.map((colConfig) => {
      if (!colConfig) return 'htLeft';

      // 采样前 10 行数据（跳过第一行表头行）
      const sampleSize = Math.min(10, tableData.length - 1);
      let numberCount = 0;
      let validCount = 0;

      for (let i = 1;i <= sampleSize;i++) {
        const row = tableData[i];
        if (!row) continue;
        const val = row[colConfig.data];
        if (val === null || val === undefined || val === '') continue;
        // eslint-disable-next-line no-plusplus
        validCount++;
        const vType = detectValueType(val);
        // eslint-disable-next-line no-plusplus
        if (vType === 'number') numberCount++;
      }

      // 如果超过一半的有效值是数值，则该列按数值右对齐
      if (validCount > 0 && numberCount >= validCount * 0.5) {
        return 'htRight';
      }
      return 'htLeft';
    });
  }, [columns, tableData]);

  /**
   * cells 回调函数：用于实现单元格级别的独立对齐
   * - 第一行（表头行）：跟随该列的整体类型对齐，并添加加粗样式
   * - 其他行：使用预计算的列级对齐方式（基于采样检测），避免逐单元格调用 detectValueType
   *
   * [性能优化] 通过 ref 访问 columnAlignClassNames，避免 cellsCallback 依赖 tableData/columns，
   * 从而防止每次编辑后 cells prop 引用变化触发 Handsontable 完整重绘
   */
  const columnAlignClassNamesRef = useRef(columnAlignClassNames);
  useEffect(() => { columnAlignClassNamesRef.current = columnAlignClassNames }, [columnAlignClassNames]);
  const iconSelectedRowIndexRef = useRef(iconSelectedRowIndex);
  useEffect(() => { iconSelectedRowIndexRef.current = iconSelectedRowIndex }, [iconSelectedRowIndex]);
  useEffect(() => {
    hotInstanceRef.current?.render();
  }, [iconSelectedRowIndex]);





  /**
   * 滚动事件处理函数
   * 检测滚动位置，直接操作 DOM class 显示/隐藏按钮
   * - 垂直滚动到底部 或 内容不需要垂直滚动：显示"添加行"按钮
   * - 水平滚动到最右侧 或 内容不需要水平滚动：显示"添加列"按钮
   * 完全绑过 React 状态，避免重渲染导致的抖动
   */
  const handleScroll = useCallback((e: Event) => {
    const target = e.target as HTMLElement;
    if (!target) return;
    
    const { scrollTop, scrollHeight, clientHeight, scrollLeft, scrollWidth, clientWidth } = target;
    
    // 判断是否水平滚动到最右侧（留 50px 容差），或内容无需水平滚动时直接显示
    const addColEl = addColButtonRef.current;
    if (addColEl) {
      const noHorizontalScroll = scrollWidth <= clientWidth;
      const isAtRight = noHorizontalScroll || (scrollLeft + clientWidth >= scrollWidth - 40);
      if (isAtRight) {
        addColEl.classList.add('dba-smart-table-grid__add-column--visible');
      } else {
        addColEl.classList.remove('dba-smart-table-grid__add-column--visible');
      }
    }
    
    // 判断是否垂直滚动到底部（留 50px 容差），或内容无需垂直滚动时直接显示
    const addRowEl = addRowButtonRef.current;
    if (addRowEl) {
      const noVerticalScroll = scrollHeight <= clientHeight;
      const isAtBottom = noVerticalScroll || (scrollTop + clientHeight >= scrollHeight - 40);
      if (isAtBottom) {
        addRowEl.classList.add('dba-smart-table-grid__add-row--visible');
      } else {
        addRowEl.classList.remove('dba-smart-table-grid__add-row--visible');
      }
    }
  }, []);

  // 存储滚动容器引用，用于在 afterDestroy 时移除监听
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  // 行/列拉伸 + 首列拖拽图标的根节点委托 teardown（afterDestroy 时统一清理）
  const resizeTeardownRef = useRef<(() => void) | null>(null);
  const rowDragTeardownRef = useRef<(() => void) | null>(null);

  /**
   * 行/列拉伸 + 首列拖拽图标的根节点委托：实现已下沉到 hooks/，
   * 这里只在 afterInit 调用 install(hot)、afterDestroy 调用返回的 teardown。
   */
  const installResizeHandles = useResizeHandles();
  const installRowDragDelegation = useRowDragDelegation(setIconSelectedRowIndex);

  /**
   * cells 回调：只设置 className；首列（col=0 && row>0）设 renderer 画 DOM。
   * renderer 内不挂任何监听 —— 拖拽/右键由根节点 installRowDragDelegation 统一委托。
   */
  const cellsCallback = useCallback((row: number, col: number): Handsontable.CellProperties => {
    const cellProps: Handsontable.CellProperties = {};
    if (row < 0 || col < 0) return cellProps;

    const alignClass = columnAlignClassNamesRef.current[col] || 'htLeft';

    if (row === 0) {
      cellProps.className = `${alignClass} dba-smart-table-header-row`;
      return cellProps;
    }

    cellProps.className = col === 0 ? `${alignClass} dba-smart-table-first-column-cell` : alignClass;
    if (col === 0) {
      cellProps.renderer = (_instance, td, _r, _c, _prop, value) => {
        // HOT 在自定义 renderer 分支下 cellProps.className 不总应用，这里直接 classList.add 兜底
        // 确保 padding-left: 0 的 CSS 覆盖规则能命中
        td.classList.add('dba-smart-table-first-column-cell');
        const cellValue = value === null || value === undefined ? '' : String(value);
        td.replaceChildren(createFirstColumnContent(cellValue, rowDragIcon));
      };
    }
    return cellProps;
  }, []);

  // 右键菜单处理（行/列操作转移到首列/首行）
  const handleBeforeOnCellContextMenu = useCallback((event: MouseEvent, coords: Handsontable.CellCoords) => {
    // 只读模式下禁用右键菜单
    if (readonly) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    const hot = getHotInstance();
    if (!hot) return;

    const isCell = coords.row >= 0 && coords.col >= 0;
    const isFirstRowCell = isCell && coords.row === 0;
    const isSelectedRowCell = isCell && coords.row > 0 && iconSelectedRowIndex === coords.row;

    // 阻止默认右键菜单
    event.preventDefault();
    event.stopPropagation();

    // 菜单打开前锁定选区（与 Handsontable 内置菜单行为一致）
    lockSelection();

    if (isSelectedRowCell) {
      // 检查当前选区是否已经包含多行（用户通过 Shift/Ctrl 选中多行后右键）
      const selected = hot.getSelected(); // [[startRow, startCol, endRow, endCol], ...]
      let rowIndices: number[] = [coords.row];
      if (selected && selected.length > 0) {
        const selectedRows = new Set<number>();
        selected.forEach(([startRow, , endRow]) => {
          const minRow = Math.min(startRow, endRow);
          const maxRow = Math.max(startRow, endRow);
          for (let r = minRow;r <= maxRow;r++) {
            if (r > 0) selectedRows.add(r); // 排除首行（表头行）
          }
        });
        if (selectedRows.has(coords.row) && selectedRows.size > 1) {
          rowIndices = Array.from(selectedRows).sort((a, b) => a - b);
        }
      }
      // 选中整行（与单行右键保持一致，直接调用 selectRows 即可高亮）
      const minRow = rowIndices[0];
      const maxRow = rowIndices[rowIndices.length - 1];
      hot.selectRows(minRow, maxRow);
      setContextMenu({
        visible: true,
        x: event.clientX,
        y: event.clientY,
        type: 'row',
        selectedIndices: rowIndices,
      });
    } else if (isFirstRowCell) {
      // 右键首行（语义表头行）时触发列操作菜单
      // 检查当前选区是否已经包含多列（用户通过 Shift/Ctrl 选中多列后右键）
      const selected = hot.getSelected(); // [[startRow, startCol, endRow, endCol], ...]
      let colIndices: number[] = [coords.col];
      if (selected && selected.length > 0) {
        const selectedCols = new Set<number>();
        selected.forEach(([, startCol, , endCol]) => {
          const minCol = Math.min(startCol, endCol);
          const maxCol = Math.max(startCol, endCol);
          for (let c = minCol;c <= maxCol;c++) {
            selectedCols.add(c);
          }
        });
        // 如果右键的列在已选中列范围内且选中了多列，则使用多选的列索引
        if (selectedCols.has(coords.col) && selectedCols.size > 1) {
          colIndices = Array.from(selectedCols).sort((a, b) => a - b);
        }
      }
      // 选中整列（与单列右键保持一致，直接调用 selectColumns 即可高亮）
      const minCol = colIndices[0];
      const maxCol = colIndices[colIndices.length - 1];
      hot.selectColumns(minCol, maxCol);
      setContextMenu({
        visible: true,
        x: event.clientX,
        y: event.clientY,
        type: 'column',
        selectedIndices: colIndices,
      });
    } else if (isCell) {
      // 单元格右键菜单
      hot.selectCell(coords.row, coords.col);
      setContextMenu({
        visible: true,
        x: event.clientX,
        y: event.clientY,
        type: 'cell',
        selectedIndices: [],
        cellCoords: { row: coords.row, col: coords.col },
      });
    }
  }, [getHotInstance, readonly, iconSelectedRowIndex]);

  // 左键点击表头行（row=0）时选中整列，与右键行为一致
  const handleAfterOnCellMouseDown = useCallback((event: MouseEvent, coords: Handsontable.CellCoords) => {
    // 仅处理左键点击表头行
    if (event.button !== 0 || coords.row !== 0 || coords.col < 0) return;
    const hot = getHotInstance();
    if (!hot) return;
    // 延迟执行，让 Handsontable 内部选区逻辑先完成，再覆盖为整列选中
    requestAnimationFrame(() => {
      hot.selectColumns(coords.col);
    });
  }, [getHotInstance]);

  // [性能优化] 通过 ref 访问 contextMenu 的最新坐标，避免 menuActions 依赖 x/y 坐标
  const contextMenuRef = useRef(contextMenu);
  useEffect(() => { contextMenuRef.current = contextMenu }, [contextMenu]);







  // 菜单操作集合（实现已下沉到 hooks/useMenuActions）
  const menuActions = useMenuActions({
    getHotInstance,
    contextMenu,
    contextMenuRef,
    closeContextMenu,
    syncData,
    pushColumnAction,
    tableDataRef,
    columnsRef,
    onDataChangeRef,
    clipboardDataRef,
    isInternalEditRef,
    setTableData,
    setAIColumnPopover,
  });

  // 关闭 AI 列操作弹层
  const handleAIColumnPopoverClose = useCallback(() => {
    setAIColumnPopover(prev => ({ ...prev, visible: false }));
  }, []);

  // AI 列操作弹层发送回调
  const handleAIColumnPopoverSend = useCallback((prompt: string) => {
    const { actionType, colIndex } = aiColumnPopover;
    if (colIndex >= 0 && columns[colIndex]) {
      if (actionType === AIColumnActionType.MODIFY_CONDITION) {
        onAIModifyColumnCondition?.(colIndex, columns[colIndex], prompt);
      } else if (actionType === AIColumnActionType.ADJUST_FORMAT) {
        onAIAdjustDataFormat?.(colIndex, columns[colIndex], prompt);
      }
    }
  }, [aiColumnPopover, columns, onAIModifyColumnCondition, onAIAdjustDataFormat]);

  return (
    <div className="dba-smart-table-grid">
      <div className="dba-smart-table-grid__container">
        <HotTable
          data={tableData}
          columns={hotColumns}
          colHeaders={false}
          rowHeaders={false}
          rowHeights={44}
          width="100%"
          height="100%"
          stretchH="none"
          manualColumnResize={true}
          manualRowResize={true}
          manualColumnMove={true}
          manualRowMove={true}
          readOnly={readonly}
          contextMenu={false}
          undo={!readonly}
          columnSorting={false}
          copyPaste={true}
          autoWrapRow={true}
          autoWrapCol={true}
          cells={cellsCallback}
          licenseKey="non-commercial-and-evaluation"
          afterChange={(changes, source) => {
            if (source !== 'loadData' && changes) {
              // 获取变化的列 key，用于优化类型检测（只检测变化的列）
              const changedColKeys = changes.map((change) => change?.[1] as string).filter(Boolean);
              const uniqueColKey = changedColKeys.length === 1 ? changedColKeys[0] : undefined;
              syncData(uniqueColKey);
            }
            checkUndoRedo();
          }}
          afterUndo={checkUndoRedo}
          afterRedo={checkUndoRedo}
          afterUndoStackChange={checkUndoRedo}
          afterRedoStackChange={checkUndoRedo}
          beforeOnCellContextMenu={handleBeforeOnCellContextMenu}
          afterOnCellMouseDown={handleAfterOnCellMouseDown}
          afterInit={function(this: Handsontable) {
            hotInstanceRef.current = this;
            // 绑定滚动监听
            const wtHolder = this.rootElement?.querySelector('.wtHolder') as HTMLElement;
            if (wtHolder) {
              scrollContainerRef.current = wtHolder;
              wtHolder.addEventListener('scroll', handleScroll, { passive: true });
              handleScroll({ target: wtHolder } as unknown as Event);
            }
            // 自建行/列拉伸 handle + 首列拖拽图标的根节点事件委托
            resizeTeardownRef.current = installResizeHandles(this);
            rowDragTeardownRef.current = installRowDragDelegation(this);
          }}
          afterDestroy={function() {
            if (scrollContainerRef.current) {
              scrollContainerRef.current.removeEventListener('scroll', handleScroll);
              scrollContainerRef.current = null;
            }
            resizeTeardownRef.current?.();
            resizeTeardownRef.current = null;
            rowDragTeardownRef.current?.();
            rowDragTeardownRef.current = null;
            hotInstanceRef.current = null;
          }}
          className="dba-smart-table-hot"
        />
      </div>

      {!readonly && (
        <div 
          ref={addColButtonRef}
          className="dba-smart-table-grid__add-column"
        >
          <AddRowColButton type={RowColType.COLUMN} placement="left" onAddBlank={handleAddColumn} onSmartFillConfirm={onSmartFillColumn} />
        </div>
      )}

      {!readonly && (
        <div 
          ref={addRowButtonRef}
          className="dba-smart-table-grid__add-row"
        >
          <AddRowColButton type={RowColType.ROW} placement="right" onAddBlank={handleAddRow} onSmartFillConfirm={onSmartFillRow} disableAddBlank={isMaxRowsReached} />
        </div>
      )}

      {!readonly && <TableContextMenu
        menuState={contextMenu}
        onClose={closeContextMenu}
        onCopy={menuActions.copy}
        onPaste={menuActions.paste}
        onInsertRowAbove={menuActions.insertRowAbove}
        onInsertRowBelow={menuActions.insertRowBelow}
        onClearRowContent={menuActions.clearRowContent}
        onDeleteRow={menuActions.deleteRow}
        onSortAsc={menuActions.sortAsc}
        onSortDesc={menuActions.sortDesc}
        onInsertColumnLeft={menuActions.insertColumnLeft}
        onInsertColumnRight={menuActions.insertColumnRight}
        onClearColumnContent={menuActions.clearColumnContent}
        onDeleteColumn={menuActions.deleteColumn}
        onAIModifyCondition={menuActions.aiModifyCondition}
        onAIAdjustFormat={menuActions.aiAdjustFormat}
        onClearCellContent={menuActions.clearCellContent}
        isSortable={contextMenu.type === 'column' && contextMenu.selectedIndices.length > 0 ? columns[contextMenu.selectedIndices[0]]?.sortable !== false : true}
        disableInsertRow={isMaxRowsReached}
        selectedCount={contextMenu.selectedIndices.length}
      />}

      {/* AI 列操作弹层 - 只读模式下不渲染 */}
      {!readonly && (
        <AIColumnPopover
          visible={aiColumnPopover.visible}
          actionType={aiColumnPopover.actionType}
          tableName={tableName || defaultTableName}
          sheetName={sheetName}
          colIndex={aiColumnPopover.colIndex}
          position={aiColumnPopover.position}
          onClose={handleAIColumnPopoverClose}
          onSend={handleAIColumnPopoverSend}
        />
      )}
    </div>
  );
});

SmartTableGrid.displayName = 'SmartTableGrid';

export default SmartTableGrid;
